import { useState } from 'react';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogScrollArea,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  CheckCircle, 
  Copy, 
  Chrome,
  Settings,
  FileText,
  Play
} from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

export interface ChromeExtensionSuccessConfig {
  name: string;
  icon: string;
  themeColor: string;
}

export interface ChromeExtensionSuccessModalContentProps {
  onClose: () => void;
  integration: any;
  extensionConfig: ChromeExtensionSuccessConfig;
}

export function ChromeExtensionSuccessModalContent({
  onClose,
  integration,
  extensionConfig,
}: ChromeExtensionSuccessModalContentProps) {
  const { t } = useTranslation();
  const [copiedStates, setCopiedStates] = useState<{ [key: string]: boolean }>({});

  const handleCopy = async (text: string, key: string, successMessage: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedStates(prev => ({ ...prev, [key]: true }));
      toast.success(successMessage);
      setTimeout(() => {
        setCopiedStates(prev => ({ ...prev, [key]: false }));
      }, 2000);
    } catch (err) {
      toast.error(t('chromeExtension.messages.copyFailed', { ns: 'integrations' }) as string);
    }
  };

  const handleOpenExtensionBuilder = () => {
    // Close this modal and let parent know to open Extension Builder
    onClose();
    // This will trigger the parent to open the Extension Builder modal
    // The parent component will handle this by setting showExtensionBuilder state
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('openExtensionBuilder', {
        detail: { socketId: integration.socket_id }
      }));
    }
  };

  const installationSteps = [
    t('chromeExtension.installation.step1', { ns: 'integrations' }),
    t('chromeExtension.installation.step2', { ns: 'integrations' }),
    t('chromeExtension.installation.step3', { ns: 'integrations' }),
    t('chromeExtension.installation.step4', { ns: 'integrations' }),
    t('chromeExtension.installation.step5', { ns: 'integrations' })
  ];

  return (
    <div className="flex min-h-0 flex-1 flex-col">
        <DialogHeader className="flex-shrink-0 p-6 pb-4 pr-12">
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle className="w-6 h-6 text-green-600" />
            {t('chromeExtension.success.title', { ns: 'integrations' })}
          </DialogTitle>
          <DialogDescription>
            {t('chromeExtension.success.description', { ns: 'integrations' })}
          </DialogDescription>
        </DialogHeader>

        <DialogScrollArea className="space-y-6 px-6">
          {/* Success Summary */}
          <Card className="border-green-200 bg-green-50">
            <CardHeader>
              <CardTitle className="text-lg text-green-800 flex items-center gap-2">
                <Chrome className="w-5 h-5" />
                {t('chromeExtension.success.integrationReady', { ns: 'integrations' })}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-sm text-green-700 font-medium">
                    {t('chromeExtension.success.integrationName', { ns: 'integrations' })}:
                  </span>
                  <div className="text-green-900">{integration.integration_name}</div>
                </div>
                <div>
                  <span className="text-sm text-green-700 font-medium">
                    {t('chromeExtension.success.extensionName', { ns: 'integrations' })}:
                  </span>
                  <div className="text-green-900 flex items-center gap-1">
                    <span className="text-lg">{extensionConfig.icon}</span>
                    {extensionConfig.name}
                  </div>
                </div>
                <div>
                  <span className="text-sm text-green-700 font-medium">
                    {t('chromeExtension.success.status', { ns: 'integrations' })}:
                  </span>
                  <Badge className="bg-green-600 text-white">
                    {t('chromeExtension.success.active', { ns: 'integrations' })}
                  </Badge>
                </div>
                <div>
                  <span className="text-sm text-green-700 font-medium">
                    {t('chromeExtension.success.socketId', { ns: 'integrations' })}:
                  </span>
                  <code className="text-xs bg-white px-1 rounded">{integration.socket_id}</code>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Next Steps */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Play className="w-5 h-5 text-blue-600" />
                {t('chromeExtension.success.nextSteps', { ns: 'integrations' })}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Extension Builder Button */}
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-start gap-3">
                    <Chrome className="w-6 h-6 text-blue-600 mt-0.5" />
                    <div className="flex-1">
                      <h3 className="font-medium text-blue-900 mb-1">
                        {t('chromeExtension.success.buildExtension', { ns: 'integrations' })}
                      </h3>
                      <p className="text-sm text-blue-700 mb-3">
                        {t('chromeExtension.success.buildDescription', { ns: 'integrations' })}
                      </p>
                      <Button 
                        onClick={handleOpenExtensionBuilder}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <Chrome className="w-4 h-4 mr-2" />
                        {t('chromeExtension.success.openBuilder', { ns: 'integrations' })}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Alternative: Manual Setup */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Settings className="w-6 h-6 text-gray-600 mt-0.5" />
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900 mb-1">
                        {t('chromeExtension.success.manualSetup', { ns: 'integrations' })}
                      </h3>
                      <p className="text-sm text-gray-600 mb-2">
                        {t('chromeExtension.success.manualDescription', { ns: 'integrations' })}
                      </p>
                      
                      {/* API Key Display */}
                      <div className="bg-white p-3 rounded border mt-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="text-sm font-medium">API Key:</span>
                            <code className="block text-xs bg-gray-100 p-1 rounded mt-1 break-all">
                              {integration.api_key || '••••••••••••••••••••••••••••••••'}
                            </code>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleCopy(
                              integration.api_key || '',
                              'api_key',
                              t('chromeExtension.messages.apiKeyCopied', { ns: 'integrations' }) as string
                            )}
                            disabled={!integration.api_key}
                          >
                            {copiedStates.api_key ? (
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Installation Guide */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-gray-600" />
                {t('chromeExtension.success.installationGuide', { ns: 'integrations' })}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <p className="text-sm text-gray-600 mb-4">
                  {t('chromeExtension.success.installationDescription', { ns: 'integrations' })}
                </p>
                <ol className="space-y-2">
                  {installationSteps.map((step, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-sm font-medium">
                        {index + 1}
                      </span>
                      <span className="text-sm text-gray-700">{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </CardContent>
          </Card>

          {/* Configuration Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                {t('chromeExtension.success.configuration', { ns: 'integrations' })}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-600">
                    {t('chromeExtension.success.permissions', { ns: 'integrations' })}:
                  </span>
                  <div className="mt-1 space-y-1">
                    {integration.permissions?.can_read && (
                      <Badge variant="secondary" className="mr-1">Read</Badge>
                    )}
                    {integration.permissions?.can_write && (
                      <Badge variant="secondary" className="mr-1">Write</Badge>
                    )}
                    {integration.permissions?.can_search && (
                      <Badge variant="secondary" className="mr-1">Search</Badge>
                    )}
                    {integration.permissions?.can_delete && (
                      <Badge variant="secondary" className="mr-1">Delete</Badge>
                    )}
                  </div>
                </div>
                <div>
                  <span className="font-medium text-gray-600">
                    {t('chromeExtension.success.rateLimit', { ns: 'integrations' })}:
                  </span>
                  <div className="mt-1">
                    <Badge variant="outline">
                      {integration.permissions?.rate_limits?.requests_per_hour || 1000} req/hour
                    </Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </DialogScrollArea>
        <DialogFooter variant="scrollable" className="gap-2 px-6 pb-6">
          <Button variant="outline" onClick={onClose}>
            {t('close', { ns: 'common' })}
          </Button>
          <Button
            onClick={handleOpenExtensionBuilder}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Chrome className="w-4 h-4 mr-2" />
            {t('chromeExtension.success.buildNow', { ns: 'integrations' })}
          </Button>
        </DialogFooter>
    </div>
  );
}

interface ChromeExtensionSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  integration: any;
  extensionConfig: ChromeExtensionSuccessConfig;
}

export function ChromeExtensionSuccessModal({
  isOpen,
  onClose,
  integration,
  extensionConfig,
}: ChromeExtensionSuccessModalProps) {
  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent variant="scrollable" className="max-w-3xl">
        <ChromeExtensionSuccessModalContent
          integration={integration}
          extensionConfig={extensionConfig}
          onClose={onClose}
        />
      </DialogContent>
    </Dialog>
  );
}

